import posthog from 'posthog-js';
import type { AnalyticsProvider } from '../types';
import { pageContext } from '../pageContext';

export interface PostHogConfig {
  apiKey: string;
  apiHost?: string;
  uiHost?: string;
  defaults?: string;
  autocapture?: boolean;
}

export class PostHogProvider implements AnalyticsProvider {
  readonly name = 'posthog';

  init(config: Record<string, unknown>) {
    const { apiKey, apiHost, uiHost, defaults, autocapture } = config as unknown as PostHogConfig;
    if (!apiKey) return;

    posthog.init(apiKey, {
      api_host: apiHost || '/e',
      ui_host: uiHost || 'https://us.posthog.com',
      defaults: (defaults as '2026-08-30') || '2026-08-30',
      autocapture: autocapture === false
        ? false
        : {
            capture_copied_text: true,
          },
      rageclick: true,
      capture_pageview: false,
      capture_pageleave: true,
      person_profiles: 'always',
      persistence: 'localStorage+cookie',
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        collectFonts: true,
        recordHeaders: true,
        recordBody: false,
      },
      enable_recording_console_log: true,
      capture_performance: {
        web_vitals: true,
        network_timing: true,
        web_vitals_allowed_metrics: ['LCP', 'CLS', 'FCP', 'INP'],
        web_vitals_attribution: true,
      },
      capture_heatmaps: true,
      capture_dead_clicks: true,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true,
      },
      disable_scroll_properties: false,
      // EasyPrivacy blocks URLs containing posthog-recorder.js even on a
      // first-party host. Request rec.js / dc.js; nginx maps them back.
      prepare_external_dependency_script: (script: HTMLScriptElement) => {
        script.src = script.src
          .replace(/posthog-recorder\.js/g, 'rec.js')
          .replace(/dead-clicks-autocapture\.js/g, 'dc.js');
        return script;
      },
      before_send: (event) => {
        if (!event) return event;
        event.properties = { ...pageContext(), ...event.properties };
        return event;
      },
      loaded: (ph) => {
        ph.register(pageContext());
      },
    } as Parameters<typeof posthog.init>[1]);
  }

  capture(event: string, properties?: Record<string, unknown>) {
    posthog.capture(event, properties);
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    posthog.identify(userId, traits);
  }

  reset() {
    posthog.reset();
  }

  pageView(url: string, properties?: Record<string, unknown>) {
    posthog.capture('$pageview', { $current_url: url, ...properties });
  }

  setPersonProperties(properties: Record<string, unknown>) {
    posthog.setPersonProperties(properties);
  }

  register(properties: Record<string, unknown>) {
    posthog.register(properties);
  }

  captureException(error: Error, properties?: Record<string, unknown>) {
    posthog.captureException(error, properties);
  }

  optIn() {
    posthog.opt_in_capturing();
  }

  optOut() {
    posthog.opt_out_capturing();
  }

  isFeatureEnabled(flag: string): boolean {
    return posthog.isFeatureEnabled(flag) ?? false;
  }

  getFeatureFlag(flag: string): string | boolean | undefined {
    return posthog.getFeatureFlag(flag);
  }
}
